// Reine Regeln der Signatur-Liste (Handoff Dokumente §6.4): Anzeige-Status, Fortschritt, Aktionen je Status.

import type { SignaturVorgang } from './signaturTypen';
import { darfErneutSenden, darfVonHandAbschliessen, darfZwischenstand } from '../signatur/vorgangAktionen';

export type AnzeigeStatus = 'entwurf' | 'verschickt' | 'abgeschlossen' | 'abgebrochen' | 'ausgefuellt';

export const ANZEIGE_STATUS_LABEL: Record<AnzeigeStatus, string> = {
  entwurf: 'Entwurf', verschickt: 'Verschickt', abgeschlossen: 'Abgeschlossen', abgebrochen: 'Abgebrochen', ausgefuellt: 'Ausgefüllt',
};

type Kurz = Pick<SignaturVorgang, 'status' | 'unterzeichner' | 'ist_vorlage' | 'signiert_path' | 'abgelegt_am'>;

/** Abgeschlossen ohne Unterzeichner = vom Absender ausgefüllt (Ausfüll-Flow). */
export const istAusgefuellt = (v: Pick<SignaturVorgang, 'status' | 'unterzeichner'>) => v.status === 'abgeschlossen' && (v.unterzeichner?.length ?? 0) === 0;
export const anzeigeStatus = (v: Pick<SignaturVorgang, 'status' | 'unterzeichner'>): AnzeigeStatus => (istAusgefuellt(v) ? 'ausgefuellt' : v.status);

export function fortschritt(v: Pick<SignaturVorgang, 'unterzeichner'>): { signiert: number; gesamt: number; anteil: number } {
  const gesamt = v.unterzeichner?.length ?? 0;
  const signiert = (v.unterzeichner ?? []).filter(u => u.status === 'signiert').length;
  return { signiert, gesamt, anteil: gesamt ? signiert / gesamt : 0 };
}

/** Farben der Unterzeichner in Reihenfolge (Handoff §4). */
export const UNTERZEICHNER_FARBEN = ['#c4a77d', '#3b82f6', '#10b981', '#8b5cf6'] as const;
export const unterzeichnerFarbe = (i: number) => UNTERZEICHNER_FARBEN[i % UNTERZEICHNER_FARBEN.length];

export type SignaturAktion =
  | 'bearbeiten' | 'verschicken' | 'erinnern' | 'zwischenstand' | 'vonHand' | 'abbrechen'
  | 'herunterladen' | 'email' | 'ablegen' | 'kopieren' | 'loeschen';

/** Aktionen je Status (Handoff §6.4); Erinnern/Zwischenstand/Von Hand nach den bestehenden Regeln. */
export function signaturAktionen(v: Kurz): SignaturAktion[] {
  if (v.ist_vorlage) return ['bearbeiten', 'loeschen'];
  switch (v.status) {
    case 'entwurf': return ['bearbeiten', 'verschicken', 'loeschen'];
    case 'verschickt': return [
      ...(darfErneutSenden(v) ? ['erinnern' as const] : []),
      ...(darfZwischenstand(v) ? ['zwischenstand' as const] : []),
      ...(darfVonHandAbschliessen(v) ? ['vonHand' as const] : []),
      'abbrechen', 'loeschen',
    ];
    case 'abgeschlossen': return [
      ...(v.signiert_path ? ['herunterladen' as const, 'email' as const, ...(v.abgelegt_am ? [] : ['ablegen' as const])] : []),
      'loeschen',
    ];
    case 'abgebrochen': return ['kopieren', 'loeschen'];
    default: return ['loeschen'];
  }
}

/** Was fehlt noch zum Verschicken? (gleiche Prüfungen wie die Edge Function, ohne Fixierungs-Detail) */
export function versandHinweis(v: Pick<SignaturVorgang, 'unterzeichner' | 'felder'>): string | null {
  const u = v.unterzeichner ?? [];
  if (!u.length || u.some(x => !x.name?.trim() || !x.email?.trim())) return 'Name und E-Mail für jeden Unterzeichner fehlen.';
  const f = v.felder ?? [];
  if (!f.length) return 'Es ist noch kein Feld platziert.';
  const ohne = u.findIndex((_, i) => !f.some(x => x.unterzeichner === i && x.typ === 'signatur' && x.pflicht));
  if (ohne >= 0) return `Unterzeichner ${ohne + 1} braucht ein Pflicht-Unterschriftsfeld.`;
  return null;
}

export interface BezugChip { art: 'objekt' | 'einheit' | 'mieter' | 'eigentuemer'; text: string }
export function bezugChips(bezug: Record<string, string | null> | null | undefined): BezugChip[] {
  const b = bezug ?? {};
  return ([
    ['objekt', b.objekt_name], ['einheit', b.einheit_name], ['mieter', b.mieter_name], ['eigentuemer', b.eigentuemer_name],
  ] as const).filter(([, t]) => !!(t ?? '').trim()).map(([art, t]) => ({ art, text: (t as string).trim() }));
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** „Noch offen: …“ im Editor-Fuß – Verschicken ist erst aktiv, wenn die Liste leer ist. */
export function nochOffen(e: { titel: string; unterzeichner: readonly { name: string; email: string }[]; felder: readonly { typ: string; pflicht: boolean; unterzeichner: number }[] }): string[] {
  const offen: string[] = [];
  if (!e.titel.trim()) offen.push('Titel');
  if (e.unterzeichner.length === 0) offen.push('Unterzeichner');
  e.unterzeichner.forEach((u, i) => {
    const wer = u.name.trim() || `Unterzeichner ${i + 1}`;
    if (!u.name.trim()) offen.push(`Name von Unterzeichner ${i + 1}`);
    if (!EMAIL.test(u.email.trim())) offen.push(`E-Mail von ${wer}`);
    if (!e.felder.some(f => f.unterzeichner === i && f.typ === 'signatur' && f.pflicht)) offen.push(`Unterschriftsfeld für ${wer}`);
  });
  if (e.felder.length === 0) offen.push('Felder');
  return offen;
}
